import axios from 'axios';

const API_BASE_URL =
  typeof window !== 'undefined'
    ? '/api'
    : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');

const publicApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 45000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Service pour les blogs publics
const publicBlogService = {
  getPublicBlogs: async (args = 10) => {
    try {
      const params =
        typeof args === 'number'
          ? { limit: args }
          : {
              page: args?.page,
              limit: args?.limit,
            };
      const response = await publicApi.get('/blogs/public', {
        params
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des blogs:', error);
      throw error;
    }
  },

  getBlogBySlug: async (slug) => {
    try {
      const response = await publicApi.get(`/blogs/slug/${slug}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération du blog:', error);
      throw error;
    }
  }
};

// Service pour les produits publics
const publicProductService = {
  getProducts: async (params = {}, axiosOpts = {}) => {
    try {
      // � SPEC #9/#10 : NETTOYER LES PARAMÈTRES avant envoi.
      // La cascade CATÉGORIE Excel → CRITÈRE → VALEUR ne doit servir QUE :
      //   famille (Excel famille) / fc1_name / fc1_value / search / page / limit / status / isVisible
      // TOUT LES AUTRES PARAMS (category / subcategory / cat_* / dyn_* / critere* / facet*) = SONT SUPPRIMÉS
      // pour éviter interférence entre filtre BDD parentCategoryId (legacy) et filtre facets Excel parallèle.
      const __ALLOWLIST = new Set([
        'famille','fc1_name','fc1_value','fcPairs',
        'page','limit','sortBy','sortOrder','status','isVisible','isFeatured',
        'search','minPrice','maxPrice','brand','category'  // category reste autorisé MAIS cascade le force à undefined
      ]);
      const ALLOW_FC_N = /^fc(\d+)_(name|value)$/i;
      let cleanParams = {};
      for (const key of Object.keys(params || {})) {
        if (params[key] == null) continue;
        if (params[key] === '' && !__ALLOWLIST.has(key) && !ALLOW_FC_N.test(key)) continue;
        if (key === 'subcategory' || key === 'subCategory' || key === 'lvl1' || key === 'lvl2' || key === 'lvl3') continue;
        if (/^(?:cat_|dyn_|facet|critere|criter)/i.test(key)) continue;
        if (!__ALLOWLIST.has(key) && !ALLOW_FC_N.test(key) && /\s/.test(key)) continue;
        cleanParams[key] = params[key];
      }

      // allowlist + nettoyage ci-dessus

      const response = await publicApi.get('/products/public', { params: cleanParams, ...axiosOpts });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des produits:', error);
      throw error;
    }
  },

  getProductBySlug: async (slug) => {
    try {
    const response = await publicApi.get(`/products/slug/${slug}`);
    return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération du produit:', error);
      throw error;
    }
  },

  getFacets: async (params = {}) => {
    try {
      const response = await publicApi.get('/products/public/facets', { params });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des facettes:', error);
      return { facets: {} };
    }
  },

  // Suggestions autocomplete : nom + référence, triées par pertinence
  getSuggestions: async (q, limit = 8) => {
    try {
      const response = await publicApi.get('/products/public/suggestions', {
        params: { q, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des suggestions:', error);
      return { suggestions: [] };
    }
  },

  // Décision SEO du serveur pour la page /produits courante : canonical,
  // robots, titre et description calculés par backend/seo.js (source unique).
  getSeoCanonical: async (params = {}) => {
    try {
      const response = await publicApi.get('/seo/canonical', { params });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération de la décision SEO:', error);
      return null;
    }
  }
};

// Service pour les catégories publiques
const publicCategoryService = {
  getCategories: async () => {
    try {
    const response = await publicApi.get('/categories/slug/all');
    return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des catégories:', error);
      throw error;
    }
  },

  getCategoryBySlug: async (slug) => {
    try {
    const response = await publicApi.get(`/categories/slug/${slug}`);
    return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération de la catégorie:', error);
      throw error;
    }
  },

  getBranches: async (idOrRoot = 'root', params = {}) => {
    try {
      const ref = idOrRoot == null ? 'root' : idOrRoot;
      const response = await publicApi.get(`/categories/public/${encodeURIComponent(ref)}/branches`, { params });
      return response.data;
    } catch (error) {
      console.error('Erreur branches catégories:', error);
      return { branches: [] };
    }
  },

  getFamily: async (id, params = {}) => {
    try {
      const response = await publicApi.get(`/categories/public/${encodeURIComponent(id)}/family`, { params });
      return response.data;
    } catch (error) {
      console.error('Erreur famille catégorie:', error);
      return { family: null };
    }
  }
};

// Service pour les devis publics
const publicQuoteService = {
  createQuote: async (quoteData) => {
    try {
    const response = await publicApi.post('/quotes', quoteData);
    return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du devis:', error);
      throw error;
    }
  }
};

const publicContactService = {
  sendMessage: async (contactData) => {
    try {
      const response = await publicApi.post('/contact', contactData);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'envoi du message de contact:", error);
      throw error;
    }
  }
};

// Service pour les publicités (endpoints publics)
const advertisementService = {
  // Obtenir les publicités actives
  getActiveAdvertisements: async () => {
    try {
      const response = await publicApi.get('/advertisements/active');
    return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des publicités:', error);
      throw error;
    }
  }
};

export { 
  publicBlogService, 
  publicProductService, 
  publicCategoryService, 
  publicQuoteService,
  publicContactService,
  advertisementService 
};
export default publicApi;
